import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import type { Transaction } from '@/types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Get monthly savings data for the entire year (for line chart)
 */
export async function getYearMonthlySavings(userId: string, year: number) {
  try {
    const monthsCol = collection(db, `users/${userId}/years/${year}/months`);
    const snapshot = await getDocs(monthsCol);

    const savingsMap = new Map<string, number>();
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      savingsMap.set(doc.id, data.savings || 0);
    });

    // Return data for all 12 months (even if 0)
    return MONTHS.map(month => ({
      month,
      savings: savingsMap.get(month) || 0,
    }));
  } catch (error) {
    console.error('Error getting yearly savings:', error);
    return [];
  }
}

/**
 * Get expense breakdown by category for the entire year (for pie chart)
 */
export async function getYearExpensesByCategory(userId: string, year: number) {
  try {
    const monthsCol = collection(db, `users/${userId}/years/${year}/months`);
    const monthsSnapshot = await getDocs(monthsCol);

    const categoryTotals = new Map<string, number>();

    // Iterate through each month
    for (const monthDoc of monthsSnapshot.docs) {
      const transactionsCol = collection(
        db, 
        `users/${userId}/years/${year}/months/${monthDoc.id}/transactions`
      );
      const transactionsSnapshot = await getDocs(transactionsCol);

      // Sum up expenses by category
      transactionsSnapshot.docs.forEach(doc => {
        const transaction = doc.data() as Transaction;
        if (transaction.type === 'expense') {
          const current = categoryTotals.get(transaction.categoryId) || 0;
          categoryTotals.set(transaction.categoryId, current + transaction.amount);
        }
      });
    }

    // Convert to array format
    return Array.from(categoryTotals.entries())
      .map(([categoryId, amount]) => ({
        categoryId,
        amount,
      }))
      .filter(item => item.amount > 0)
      .sort((a, b) => b.amount - a.amount); // Sort by amount descending
  } catch (error) {
    console.error('Error getting expenses by category:', error);
    return [];
  }
}